/**
 * The three appendix boards: verification errands, the vendor BAA matrix, and the trust-list
 * crosswalk. All table-shaped, so they share the markup conventions of
 * UserFeedback/components/FeedbackSubmissionsTable.
 */
import { useMemo, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import Button from '../../../components/Button';
import type { HipaaItem, HipaaStatus } from '../types/hipaaTypes';
import { deriveCrosswalkStatus, isOpen } from '../utils/hipaaStats';
import { BlockedBadge, PriorityBadge, StatusPill, VerificationBadge } from './Badges';

const TH = 'px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';
const TD = 'px-4 py-2.5 text-sm text-gray-700 align-top';

const TableShell = ({
  headers,
  children,
}: {
  headers: string[];
  children: React.ReactNode;
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {headers.map((header) => (
            <th key={header} className={TH}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">{children}</tbody>
    </table>
  </div>
);

const OpenOnlyToggle = ({
  openOnly,
  onChange,
  shown,
  total,
}: {
  openOnly: boolean;
  onChange: (value: boolean) => void;
  shown: number;
  total: number;
}) => (
  <div className="flex items-center gap-3 mb-3">
    <Button size="sm" variant={openOnly ? 'primary' : 'outline'} onClick={() => onChange(!openOnly)}>
      Open only
    </Button>
    <span className="text-xs text-gray-400">
      {shown} of {total}
    </span>
  </div>
);

export const ErrandsTab = ({
  items,
  onOpenItem,
}: {
  items: HipaaItem[];
  onOpenItem: (item: HipaaItem) => void;
}) => {
  const [openOnly, setOpenOnly] = useState(false);
  const visible = openOnly ? items.filter((item) => isOpen(item.status)) : items;

  return (
    <div>
      <p className="text-sm text-gray-500 mb-3">
        Console and vendor checks that cannot be answered from the repo. Each one unblocks the
        sections it feeds.
      </p>
      <OpenOnlyToggle
        openOnly={openOnly}
        onChange={setOpenOnly}
        shown={visible.length}
        total={items.length}
      />
      <TableShell headers={['', 'Check', 'Feeds', 'Status', '']}>
        {visible.map((item) => (
          <tr
            key={item.id}
            className="hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => onOpenItem(item)}
          >
            <td className={`${TD} font-mono text-xs text-gray-400 w-12`}>
              {item.id.replace('err-', '').toUpperCase()}
            </td>
            <td className={TD}>{item.title}</td>
            <td className={`${TD} whitespace-nowrap text-xs text-gray-500`}>
              {item.mapsTo.map((ref) => `§${ref}`).join(', ')}
            </td>
            <td className={TD}>
              <StatusPill status={item.status} />
            </td>
            <td className={`${TD} text-right`}>
              <span className="inline-flex items-center gap-1.5">
                {item.blocked && <BlockedBadge reason={item.blockedReason} />}
                {item.commentCount > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-400">
                    <MessageSquare size={11} />
                    {item.commentCount}
                  </span>
                )}
              </span>
            </td>
          </tr>
        ))}
      </TableShell>
    </div>
  );
};

export const VendorsTab = ({
  items,
  onOpenItem,
}: {
  items: HipaaItem[];
  onOpenItem: (item: HipaaItem) => void;
}) => {
  const [openOnly, setOpenOnly] = useState(false);
  const visible = openOnly ? items.filter((item) => isOpen(item.status)) : items;

  return (
    <div>
      <p className="text-sm text-gray-500 mb-3">
        Every vendor, what health data it receives, and where its BAA stands. Assumes the
        &ldquo;treat all user data as if PHI&rdquo; posture from §1.2.
      </p>
      <OpenOnlyToggle
        openOnly={openOnly}
        onChange={setOpenOnly}
        shown={visible.length}
        total={items.length}
      />
      <TableShell headers={['Vendor', 'Health data received', 'BAA', 'Disposition', 'Status']}>
        {visible.map((item) => (
          <tr
            key={item.id}
            className="hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => onOpenItem(item)}
          >
            <td className={`${TD} font-medium text-gray-900 max-w-xs`}>
              {item.title}
              {item.needsVerification && (
                <span className="ml-1.5 inline-block align-middle">
                  <VerificationBadge />
                </span>
              )}
            </td>
            <td className={`${TD} text-xs text-gray-500 max-w-xs`}>{item.vendor?.healthData}</td>
            <td className={`${TD} text-xs`}>{item.vendor?.baaStatus}</td>
            <td className={`${TD} text-xs text-gray-500 max-w-xs`}>{item.vendor?.disposition}</td>
            <td className={TD}>
              <span className="inline-flex items-center gap-1.5">
                <StatusPill status={item.status} />
                <PriorityBadge priorities={item.priorities} />
              </span>
            </td>
          </tr>
        ))}
      </TableShell>
    </div>
  );
};

export const TrustListTab = ({
  items,
  allItems,
  onOpenItem,
}: {
  items: HipaaItem[];
  allItems: HipaaItem[];
  onOpenItem: (item: HipaaItem) => void;
}) => {
  const [openOnly, setOpenOnly] = useState(false);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        item,
        status: deriveCrosswalkStatus(item, allItems),
        derived: !item.statusIsExplicit && item.mapsTo.length > 0,
      })),
    [items, allItems],
  );

  const visible = openOnly ? rows.filter((row) => isOpen(row.status)) : rows;
  const done = rows.filter((row) => row.status === 'done').length;
  const applicable = rows.filter((row) => row.status !== ('na' as HipaaStatus)).length;

  return (
    <div>
      <p className="text-sm text-gray-500 mb-3">
        The controls a partner security review scores us on. Status rolls up automatically from
        the sections each control follows — {done} of {applicable} are green. Opening a row lets
        you pin it to a fixed status or leave a note.
      </p>
      <OpenOnlyToggle
        openOnly={openOnly}
        onChange={setOpenOnly}
        shown={visible.length}
        total={rows.length}
      />
      <TableShell headers={['Control', 'Follows', 'Status', '']}>
        {visible.map(({ item, status, derived }) => (
          <tr
            key={item.id}
            className="hover:bg-gray-50 cursor-pointer transition-colors"
            onClick={() => onOpenItem(item)}
          >
            <td className={`${TD} text-gray-900`}>
              {item.title}
              {item.statusNote && (
                <span className="block text-xs text-gray-400 mt-0.5">{item.statusNote}</span>
              )}
            </td>
            <td className={`${TD} whitespace-nowrap text-xs text-gray-500`}>
              {item.mapsTo.length > 0 ? item.mapsTo.map((ref) => `§${ref}`).join(', ') : '—'}
            </td>
            <td className={TD}>
              <StatusPill status={status} derived={derived} />
            </td>
            <td className={`${TD} text-right`}>
              {item.commentCount > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-400">
                  <MessageSquare size={11} />
                  {item.commentCount}
                </span>
              )}
            </td>
          </tr>
        ))}
      </TableShell>
    </div>
  );
};
